import { OpeningHours, Restaurant } from "api-types";

import {
  FirestoreLocationDocument,
  NonPrimaryLocation,
  PrimaryLocation,
  RestaurantDraft,
} from "../types";

/**
 * This file contains utilities that help translating between the Restaurant <> Location models,
 * including Primary and NonPrimary locations, firestore documents, and the fields that are allowed
 * to be updated.
 */

// exported for tests
export const disambiguatedPrimaryRestaurantName: (
  restaurant: Restaurant,
  nonPrimaryLocations: NonPrimaryLocation[]
) => string = (restaurant, nonPrimaryLocations) => {
  const primaryLocationName = restaurant.name;
  const primaryLocationNameIsUnique = nonPrimaryLocations.length === 0;
  const nonPrimaryLocationsExistInSameCity =
    nonPrimaryLocations.filter(
      (location) => location.address.city === restaurant.address.city
    ).length > 0;
  if (primaryLocationNameIsUnique) {
    return primaryLocationName;
  }
  if (!nonPrimaryLocationsExistInSameCity) {
    return restaurant.address.city;
  }
  return restaurant.address.street || restaurant.address.city;
};

export const primaryLocationFromRestaurant: (
  restaurant: Restaurant,
  // these are used to determine how the primary location should be named.
  nonPrimaryLocations: NonPrimaryLocation[]
) => PrimaryLocation = (
  restaurant: Restaurant,
  nonPrimaryLocations: NonPrimaryLocation[]
) => ({
  id: null, // The primary location is the restaurant itself.
  restaurantUuid: restaurant.uuid,
  name: disambiguatedPrimaryRestaurantName(restaurant, nonPrimaryLocations),
  address: restaurant.address,
  e164PhoneNumber: restaurant.e164PhoneNumber || null,
  formattedPhoneNumber: restaurant.formattedPhoneNumber || null,
  contactEmail: restaurant.contactEmail || null,
  openingHours: restaurant.openingHours || [],
  coverImageKey: restaurant.coverImageKey || null,
  coverImageUrl: restaurant.coverImageUrl || null,
  isPrimary: true,
});

// Compat function to create the primary location when to locations are present.
// Required when trying to save the primary location.
export const toDraftRestaurant: (
  primaryLocation: PrimaryLocation
) => Partial<RestaurantDraft> = (primaryLocation: PrimaryLocation) => ({
  address: {
    street: primaryLocation.address.street || null,
    city: primaryLocation.address.city || null,
    postalCode: primaryLocation.address.postalCode || null,
  },
  e164PhoneNumber: primaryLocation.e164PhoneNumber,
  formattedPhoneNumber: primaryLocation.formattedPhoneNumber,
  contactEmail: primaryLocation.contactEmail,
  openingHours: primaryLocation.openingHours,
  coverImageKey: primaryLocation.coverImageKey,
  coverImageUrl: primaryLocation.coverImageUrl,
});

export const nonPrimaryLocationFromFirestoreDocument: (
  firestoreLocationDocument: FirestoreLocationDocument
) => NonPrimaryLocation = (
  firestoreLocationDocument: FirestoreLocationDocument
) => ({
  id: firestoreLocationDocument.id,
  restaurantUuid: firestoreLocationDocument.restaurantUuid,
  name: firestoreLocationDocument.name,
  address: firestoreLocationDocument.address,
  e164PhoneNumber: firestoreLocationDocument.e164PhoneNumber,
  formattedPhoneNumber: firestoreLocationDocument.formattedPhoneNumber,
  contactEmail: firestoreLocationDocument.contactEmail,
  openingHours: firestoreLocationDocument.openingHours,
  coverImageKey: firestoreLocationDocument.coverImageKey,
  coverImageUrl: firestoreLocationDocument.coverImageUrl,
  isPrimary: false,
});

export const newNonPrimaryLocation: (
  restaurant: Restaurant,
  fields: {
    id: string;
    name: string;
    address: {
      street: string;
      city: string;
      postalCode: string;
    };
    e164PhoneNumber: string | null;
    formattedPhoneNumber: string | null;
    contactEmail: string | null;
    openingHours: OpeningHours[];
    coverImageKey: string | null;
    coverImageUrl: string | null;
  }
) => NonPrimaryLocation = (restaurant, fields) => ({
  id: fields.id,
  restaurantUuid: restaurant.uuid,
  name: fields.name,
  address: {
    ...fields.address,
    countryCode: restaurant.address.countryCode,
  },
  e164PhoneNumber: fields.e164PhoneNumber,
  formattedPhoneNumber: fields.formattedPhoneNumber,
  contactEmail: fields.contactEmail,
  openingHours: fields.openingHours,
  coverImageKey: fields.coverImageKey,
  coverImageUrl: fields.coverImageUrl,
  isPrimary: false,
});
